import React, { useState, useMemo } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import api, { apiError } from "@/lib/api";
import { money, shortDate } from "@/lib/format";
import { toast } from "sonner";
import { StepFlow } from "@/components/StepFlow";
import { Broadcast as BroadcastIcon, ChatCircleText, WhatsappLogo, EnvelopeSimple, Megaphone, ShareNetwork, Sparkle, PaperPlaneTilt } from "@phosphor-icons/react";

const CHANNELS = [
  { id: "sms", label: "Text (SMS)", icon: ChatCircleText, unit: 4, live: true },
  { id: "whatsapp", label: "WhatsApp", icon: WhatsappLogo, unit: 3 },
  { id: "email", label: "Email", icon: EnvelopeSimple, unit: 1 },
  { id: "voice", label: "Voice", icon: Megaphone, unit: 6 },
  { id: "social", label: "Social", icon: ShareNetwork, unit: 0 },
];

const parseRecipients = (raw) =>
  Array.from(new Set(raw.split(/[\n,;\s]+/).map((s) => s.trim()).filter(Boolean)));

export default function Broadcast() {
  const qc = useQueryClient();
  const [step, setStep] = useState(0);
  const [channel, setChannel] = useState(CHANNELS[0]);
  const [content, setContent] = useState("");
  const [name, setName] = useState("");
  const [audience, setAudience] = useState(500);
  const [recipients, setRecipients] = useState("");
  const [goal, setGoal] = useState("");
  const [drafting, setDrafting] = useState(false);
  const [busy, setBusy] = useState(false);

  const { data: campaigns } = useQuery({ queryKey: ["campaigns"], queryFn: async () => (await api.get("/broadcast/campaigns")).data });
  const { data: mediaData } = useQuery({ queryKey: ["media"], queryFn: async () => (await api.get("/media/list")).data });
  const mediaList = mediaData?.media || [];
  const [mediaId, setMediaId] = useState("");
  const [consent, setConsent] = useState(false);

  const isSms = channel.id === "sms";
  const isVoice = channel.id === "voice";
  const isEmail = channel.id === "email";
  const usesRecipients = isSms || isVoice;
  const recipientList = useMemo(() => parseRecipients(recipients), [recipients]);
  const audienceCount = usesRecipients ? recipientList.length : Number(audience) || 0;
  const estCost = channel.unit * audienceCount;
  const audioMedia = mediaList.filter((m) => m.kind === "audio");

  const aiDraft = async () => {
    if (!goal) { toast.error("Describe your campaign goal first"); return; }
    setDrafting(true);
    try {
      const { data } = await api.post("/broadcast/ai-draft", { channel: channel.id, goal, tone: "professional" });
      setContent(data.draft);
      toast.success("SI draft ready — review before sending");
    } catch (e) { toast.error(apiError(e)); } finally { setDrafting(false); }
  };

  const reset = () => { setStep(0); setContent(""); setName(""); setGoal(""); setRecipients(""); setMediaId(""); setConsent(false); };

  const launch = async () => {
    setBusy(true);
    try {
      if (isSms) {
        const { data } = await api.post("/broadcast/sms", { name: name || "SMS Blast", content, recipients: recipientList });
        if (data.mode === "LIVE") toast.success(`Sent ${data.sent} of ${data.sent + data.failed} messages`);
        else toast.message("Saved (preview)", { description: data.note });
        if (data.failed > 0) toast.warning(`${data.failed} message(s) failed — please check the recipient numbers`);
      } else if (isVoice) {
        const { data } = await api.post("/voice/broadcast", { name: name || "Voice Broadcast", media_id: mediaId, recipients: recipientList, consent });
        if (data.sent > 0) toast.success(`Queued ${data.sent} call(s)${data.failed ? `, ${data.failed} failed` : ""}`);
        else toast.warning(data.note || "No calls were placed");
      } else {
        const { data } = await api.post("/broadcast/create", { name: name || "Untitled campaign", channel: channel.id, content, audience_size: audienceCount, sender_id: "IYENEH", media_id: mediaId || null });
        if (isEmail) toast.success(data.note || "Email broadcast sent");
        else toast.success("Broadcast sent (preview)");
      }
      qc.invalidateQueries({ queryKey: ["wallet"] });
      qc.invalidateQueries({ queryKey: ["campaigns"] });
      reset();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const canReview = isVoice ? (recipientList.length > 0 && !!mediaId && consent) : isSms ? recipientList.length > 0 : isEmail ? !!content.trim() : audienceCount > 0;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display text-3xl font-semibold tracking-tight sm:text-4xl">Broadcast Center</h1>
        <p className="mt-2 text-muted-foreground">Content → Audience → Broadcast. <span className="font-medium text-primary">SMS sends live</span>; other channels are in preview.</p>
      </div>
      <StepFlow steps={["Content", "Audience", "Broadcast"]} current={step} />

      {step === 0 && (
        <div className="animate-fade-up grid gap-6 lg:grid-cols-3">
          <div className="lg:col-span-2 space-y-4">
            <div>
              <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Channel</p>
              <div className="flex flex-wrap gap-2">
                {CHANNELS.map((c) => (
                  <button key={c.id} data-testid={`channel-${c.id}`} onClick={() => setChannel(c)}
                    className={`flex items-center gap-2 rounded-full px-4 py-2 text-sm font-medium transition-colors ${channel.id === c.id ? "bg-primary text-primary-foreground" : "border border-border"}`}>
                    <c.icon size={16} weight="bold" /> {c.label}
                    {c.live && <span className={`ml-1 rounded-full px-1.5 py-0.5 text-[10px] font-bold ${channel.id === c.id ? "bg-primary-foreground/20" : "bg-emerald-500/15 text-emerald-600"}`}>LIVE</span>}
                  </button>
                ))}
              </div>
            </div>
            <input data-testid="campaign-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Campaign name"
              className="w-full rounded-xl border border-input bg-card px-4 py-3 outline-none focus:border-primary" />

            <div className="rounded-2xl border border-primary/30 bg-accent/50 p-4">
              <div className="flex items-center gap-2 text-sm font-semibold text-accent-foreground"><Sparkle size={16} weight="fill" /> SI Campaign Assistant</div>
              <div className="mt-2 flex flex-wrap gap-2">
                <input data-testid="ai-goal" value={goal} onChange={(e) => setGoal(e.target.value)} placeholder="e.g. Promote 20% off weekend eSIM plans"
                  className="min-w-0 flex-1 basis-full rounded-xl border border-input bg-card px-3 py-2.5 text-sm outline-none focus:border-primary sm:basis-0" />
                <button data-testid="ai-draft-btn" onClick={aiDraft} disabled={drafting}
                  className="whitespace-nowrap rounded-xl bg-primary px-4 py-2.5 text-sm font-semibold text-primary-foreground disabled:opacity-60">
                  {drafting ? "Drafting…" : "Draft"}
                </button>
              </div>
            </div>

            <textarea data-testid="campaign-content" value={content} onChange={(e) => setContent(e.target.value)} rows={5}
              placeholder="Write your message, or use the SI assistant above…"
              className="w-full rounded-xl border border-input bg-card px-4 py-3 outline-none focus:border-primary" />
            {isSms && (
              <p className="text-xs text-muted-foreground">{content.length} chars · ~{Math.max(1, Math.ceil(content.length / 160))} segment(s). Include “Reply STOP to opt out”.</p>
            )}
            {!isSms && (
              <div className="rounded-2xl border border-border bg-card p-4">
                <div className="flex items-center justify-between">
                  <label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{isVoice ? "Audio to play (required)" : "Attach media (optional)"}</label>
                  <a href="/app/media" className="text-xs font-medium text-primary hover:underline">Manage media →</a>
                </div>
                <select data-testid="broadcast-media" value={mediaId} onChange={(e) => setMediaId(e.target.value)}
                  className="mt-2 w-full rounded-xl border border-input bg-card px-3 py-2.5 text-sm outline-none focus:border-primary">
                  <option value="">{isVoice ? "Select an audio file" : "No media"}</option>
                  {(isVoice ? audioMedia : mediaList).map((m) => (
                    <option key={m.id} value={m.id}>{`${m.title} · ${m.kind}`}</option>
                  ))}
                </select>
                {isVoice
                  ? (audioMedia.length === 0 && <p className="mt-1.5 text-xs text-amber-600">No audio yet — upload an MP3/WAV in Media Studio first.</p>)
                  : (mediaList.length === 0 && <p className="mt-1.5 text-xs text-muted-foreground">No media yet — add audio, video or a YouTube link in Media Studio.</p>)}
                {isVoice && <p className="mt-1.5 text-xs text-muted-foreground">A robocall dials each recipient and plays this audio. Dialing requires account verification.</p>}
              </div>
            )}
            <button data-testid="content-next" disabled={isVoice ? !mediaId : !content} onClick={() => setStep(1)}
              className="w-full rounded-full bg-primary py-3.5 font-semibold text-primary-foreground disabled:opacity-50">Continue</button>
          </div>
          <div className="rounded-2xl border border-border bg-card p-4">
            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Preview</p>
            <div className="mt-3 rounded-xl bg-secondary p-4 text-sm">{content || <span className="text-muted-foreground">Your message preview…</span>}</div>
          </div>
        </div>
      )}

      {step === 1 && (
        <div className="animate-fade-up mx-auto max-w-md rounded-3xl border border-border bg-card p-6">
          <button onClick={() => setStep(0)} className="mb-4 rounded-full border border-border px-3 py-1.5 text-sm">← Content</button>
          {usesRecipients ? (
            <>
              <label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Recipients (E.164, one per line or comma-separated)</label>
              <textarea data-testid="sms-recipients" rows={6} value={recipients} onChange={(e) => setRecipients(e.target.value)}
                placeholder={"+12025551234\n+14155550100"}
                className="mt-2 w-full rounded-xl border border-input bg-card px-4 py-3 font-mono-metric text-sm outline-none focus:border-primary" />
              <p className="mt-1 text-xs text-muted-foreground"><span data-testid="recipient-count" className="font-semibold text-foreground">{recipientList.length}</span> valid recipient(s).{isSms ? " US/Canada only." : ""}</p>
              {isVoice && (
                <label className="mt-3 flex items-start gap-2 text-xs text-muted-foreground">
                  <input data-testid="voice-consent" type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} className="mt-0.5" />
                  <span>I confirm I have consent to place prerecorded calls to these recipients, and will honor opt-outs and local calling hours.</span>
                </label>
              )}
            </>
          ) : isEmail ? (
            <div data-testid="email-audience-note" className="rounded-2xl border border-emerald-500/30 bg-emerald-500/5 p-4">
              <div className="flex items-center gap-2 text-sm font-semibold text-emerald-600"><EnvelopeSimple size={16} weight="fill" /> Sends live to all members</div>
              <p className="mt-2 text-sm text-muted-foreground">This email is delivered through your live email service to every member with an email on file. <span className="font-semibold text-foreground">Free for your account — no wallet charge.</span></p>
            </div>
          ) : (
            <>
              <label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Audience size</label>
              <input data-testid="audience-size" type="number" min="1" value={audience} onChange={(e) => setAudience(e.target.value)}
                className="mt-2 w-full rounded-xl border border-input bg-card px-4 py-3 font-mono-metric outline-none focus:border-primary" />
            </>
          )}
          {!isEmail && (
            <div className="mt-4 space-y-2 rounded-xl bg-secondary p-4 text-sm">
              <div className="flex justify-between"><span className="text-muted-foreground">Channel</span><span className="font-medium">{channel.label}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Per recipient</span><span className="font-mono-metric">{money(channel.unit)}</span></div>
              <div className="flex justify-between border-t border-border pt-2"><span className="font-semibold">Estimated cost</span><span data-testid="est-cost" className="font-mono-metric font-semibold">{money(estCost)}</span></div>
            </div>
          )}
          <button data-testid="audience-next" disabled={!canReview} onClick={() => setStep(2)}
            className="mt-5 w-full rounded-full bg-primary py-3.5 font-semibold text-primary-foreground disabled:opacity-50">Review broadcast</button>
        </div>
      )}

      {step === 2 && (
        <div className="animate-fade-up mx-auto max-w-md rounded-3xl border border-border bg-card p-6">
          <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-accent text-primary"><BroadcastIcon size={28} weight="duotone" /></div>
          <h2 className="font-display text-xl font-semibold">Confirm broadcast</h2>
          {isSms && <p className="mt-1 text-sm text-emerald-600">Live send from your number.</p>}
          <dl className="mt-4 space-y-3 text-sm">
            {[["Campaign", name || "Untitled"], ["Channel", channel.label], ["Audience", isEmail ? "All members (free)" : `${audienceCount} recipient${audienceCount === 1 ? "" : "s"}`], ["Sender", isSms ? "iYeneh Connect" : "IYENEH"]].map(([k, v]) => (
              <div key={k} className="flex justify-between"><dt className="text-muted-foreground">{k}</dt><dd className="font-medium">{v}</dd></div>
            ))}
            {!isEmail && <div className="flex justify-between border-t border-border pt-3"><dt className="font-semibold">Total cost</dt><dd className="font-mono-metric font-semibold">{money(estCost)}</dd></div>}
          </dl>
          <div className="mt-6 flex gap-3">
            <button onClick={() => setStep(1)} className="rounded-full border border-border px-5 py-3 text-sm font-medium">Back</button>
            <button data-testid="broadcast-now" onClick={launch} disabled={busy}
              className="flex flex-1 items-center justify-center gap-2 rounded-full bg-primary py-3 font-semibold text-primary-foreground transition-transform hover:scale-[1.02] disabled:opacity-60">
              <PaperPlaneTilt size={18} weight="bold" /> {busy ? "Sending…" : isSms ? "Send SMS Blast" : "Broadcast Now"}
            </button>
          </div>
        </div>
      )}

      {campaigns?.campaigns?.length > 0 && (
        <div>
          <h2 className="mb-3 mt-4 text-xs font-semibold uppercase tracking-[0.16em] text-muted-foreground">Recent campaigns</h2>
          <div className="space-y-2">
            {campaigns.campaigns.slice(0, 6).map((c) => (
              <div key={c.id} className="flex items-center justify-between rounded-xl border border-border bg-card px-4 py-3">
                <div>
                  <p className="text-sm font-medium">{c.name} {c.mode === "LIVE" && <span className="ml-1 rounded-full bg-emerald-500/15 px-1.5 py-0.5 text-[10px] font-bold text-emerald-600">LIVE</span>}</p>
                  <p className="text-xs text-muted-foreground capitalize">{c.channel} · {c.audience_size} recipients · {shortDate(c.created_at)}</p>
                </div>
                <div className="text-right"><p className="font-mono-metric text-sm font-semibold">{money(c.cost_cents)}</p><p className="text-xs text-emerald-600">{c.delivered} delivered</p></div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
